// frontend/src/components/v2/tools/generation/ModelSelector.tsx
import { useEffect, useState } from 'react';
import apiClient from '@/lib/api-client';

export interface StudioModel {
  code: string;
  display_name: string;
  is_default: boolean;
  config: {
    supported_aspect_ratios: string[];
    supported_resolutions: string[];
    max_reference_images: number;
    quality_levels?: string[]; // e.g. ['medium','high'] — gpt-image-2
    default_quality?: string;
  };
}

interface Props {
  selectedModel: StudioModel | null;
  onModelChange: (model: StudioModel) => void;
}

export function ModelSelector({ selectedModel, onModelChange }: Props) {
  const [models, setModels] = useState<StudioModel[]>([]);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    apiClient
      .get<{ models?: StudioModel[] } | StudioModel[]>('/api/v2/studio/models')
      .then(res => {
        const data = res.data;
        const list = Array.isArray(data) ? data : (data.models ?? []);
        setModels(list);
        if (!selectedModel && list.length > 0) {
          const def = list.find((m: StudioModel) => m.is_default) ?? list[0];
          onModelChange(def);
        }
      })
      .catch(err => {
        console.error('Failed to load studio models:', err);
        setError(true);
      });
  }, [onModelChange, selectedModel]);

  if (error) return (
    <p className="text-xs text-destructive">Modelle konnten nicht geladen werden</p>
  );

  if (models.length <= 1) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Modell
      </label>
      <div className="flex gap-2">
        {models.map(m => (
          <button
            key={m.code}
            type="button"
            onClick={() => onModelChange(m)}
            className={`flex-1 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
              selectedModel?.code === m.code
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-background hover:border-primary/50'
            }`}
          >
            <span className="font-medium">{m.display_name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
